import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { User, ArrowRight, Loader } from 'lucide-react';
import axios from 'axios';
import CreatorOnboarding from '../components/CreatorOnboarding';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const ProfileRedirect = () => {
  const { user, token, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [showCreatorOnboarding, setShowCreatorOnboarding] = useState(false);

  // Helper to safely check if user is a creator
  const isCreatorRole = (userObj) => {
    if (!userObj || !userObj.role) return false;
    return ['artist', 'author', 'creator'].includes(userObj.role);
  };

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;

    // If no user after auth loads, redirect to login
    if (!user) {
      console.warn('[ProfileRedirect] User is null, redirecting to auth');
      const urlParams = new URLSearchParams(location.search);
      const redirectUrl = urlParams.get('redirect');
      
      if (redirectUrl) {
        navigate(`/auth?redirect=${encodeURIComponent(redirectUrl)}`);
      } else {
        navigate('/auth');
      }
      return;
    }
    
    checkAndRedirectProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate, location]);

  const checkAndRedirectProfile = async () => {
    // Double-check user exists before any operations
    if (!user) {
      console.warn('[ProfileRedirect] checkAndRedirectProfile called with null user');
      setLoading(false);
      return;
    }

    try {
      console.log('[ProfileRedirect] Checking profile for user:', user.email, 'handle:', user.handle);
      
      // First check if user already has a handle
      if (user.handle) {
        console.log('[ProfileRedirect] User has handle, redirecting to /u/' + user.handle);
        navigate(`/u/${user.handle}`, { replace: true });
        return;
      }

      console.log('[ProfileRedirect] No handle found, checking database...');
      
      // No handle, check if profile exists in database
      const isCreator = isCreatorRole(user);
      const profileEndpoint = isCreator ? '/creators/me/profile' : `/reader/profile/${user.id}`;
      
      console.log('[ProfileRedirect] Checking endpoint:', profileEndpoint);
      
      try {
        const response = await axios.get(`${API}${profileEndpoint}`, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: 10000 // 10 second timeout
        });
        const profile = response.data.profile || response.data;
        
        console.log('[ProfileRedirect] Profile response:', profile);
        
        if (profile && profile.handle) {
          console.log('[ProfileRedirect] Profile exists with handle:', profile.handle);
          navigate(`/u/${profile.handle}`, { replace: true });
          return;
        } else {
          console.log('[ProfileRedirect] Profile exists but no handle, showing setup');
          setProfileExists(true);
        }
      } catch (error) {
        console.log('[ProfileRedirect] API error:', error.response?.status, error.message);
        if (error.response?.status === 404) {
          console.log('[ProfileRedirect] Profile not found, showing creation screen');
          setProfileExists(false);
        } else {
          // Network error or other issue - fail soft, show creation screen
          console.warn('[ProfileRedirect] Network error, showing creation screen:', error.message);
          setProfileExists(false);
        }
      }
      
    } catch (error) {
      console.error('[ProfileRedirect] Error checking profile:', error);
      // Fail soft - show creation screen instead of crashing
      setProfileExists(false);
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async () => {
    // Guard against null user
    if (!user) {
      console.warn('[ProfileRedirect] createProfile called with null user');
      return;
    }

    // Show the multi-step Creator Onboarding wizard
    setShowCreatorOnboarding(true);
  };

  const goToProfileEditor = () => {
    // Guard against null user
    if (!user) {
      navigate('/auth');
      return;
    }
    
    const profileType = isCreatorRole(user) ? 'creator' : 'reader';
    navigate(`/profile/editor/${profileType}`);
  };

  // Show loading while auth is initializing
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-16 h-16 text-orange-500 animate-spin mx-auto mb-4" />
          <div className="text-xl font-medium">Loading your profile...</div>
        </div>
      </div>
    );
  }

  // If user is null after loading, show error state (shouldn't happen with proper guards)
  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-medium mb-4">Session expired</div>
          <Button onClick={() => navigate('/auth')} className="bg-orange-500">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (showCreatorOnboarding) {
    return (
      <CreatorOnboarding 
        onComplete={(result) => {
          setShowCreatorOnboarding(false);

          // Update auth user in context to reflect new creator role
          try {
            if (user && token && login) {
              const updatedUser = {
                ...user,
                role: 'creator',
                creator_profile: result?.creator_profile || user.creator_profile,
              };
              login(updatedUser, token);
            }
          } catch (e) {
            console.error('Failed to update auth context after onboarding', e);
          }

          // After onboarding completes, send the creator to their new profile
          const handle = result?.creator_profile?.handle;
          if (handle) {
            navigate(`/u/${handle}`);
          } else {
            // Fallback: reload profile redirect flow
            setTimeout(() => {
              checkAndRedirectProfile();
            }, 1000);
          }
        }}
        onCancel={() => {
          setShowCreatorOnboarding(false);
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
      <Card className="bg-gray-900 border-gray-700 w-full max-w-md">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto mb-4 p-4 bg-orange-500/20 rounded-full w-20 h-20 flex items-center justify-center">
            <User className="w-10 h-10 text-orange-400" />
          </div>
          
          <CardTitle className="text-2xl font-bold text-white">
            {profileExists ? 'Complete Your Profile' : 'Create Your Trap Comix Profile'}
          </CardTitle>
          
          <CardDescription className="text-gray-400 text-lg">
            {profileExists 
              ? 'Your profile needs a few finishing touches to get started!'
              : 'Join the Trap Comix community and showcase your creative identity!'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-white font-semibold mb-2">Your Profile Will Include:</h3>
            <ul className="text-sm text-gray-300 space-y-2">
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Customizable banner and avatar</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Personal bio and social links</span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>
                  {isCreatorRole(user)
                    ? 'Series showcase and creator tools'
                    : 'Reading library and community features'
                  }
                </span>
              </li>
              <li className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                <span>Fan wall for community interaction</span>
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={createProfile}
              disabled={creating}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3"
            >
              {creating ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  {profileExists ? 'Setting up...' : 'Creating Profile...'}
                </>
              ) : (
                <>
                  <User className="w-4 h-4 mr-2" />
                  {profileExists ? 'Complete Profile Setup' : 'Create My Profile'}
                </>
              )}
            </Button>

            {profileExists && (
              <Button 
                onClick={goToProfileEditor}
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <ArrowRight className="w-4 h-4 mr-2" />
                Customize in Profile Editor
              </Button>
            )}

            <Button 
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full text-gray-400 hover:text-white"
            >
              Skip for Now
            </Button>
          </div>

          <div className="text-center pt-4">
            <p className="text-xs text-gray-500">
              You can always customize your profile later from the navigation menu.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfileRedirect;
